import {
  BellIcon,
  CashIcon,
  CogIcon,
  CurrencyDollarIcon,
  DesktopComputerIcon,
  ExclamationIcon,
  TagIcon,
  UsersIcon
} from "@heroicons/react/outline";
import { compact } from "lodash";
import React, { useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import NavLink from "../../../components/NavLink";
import { useMembers } from "../../../context/members";
import { useWorkspaces } from "../../../context/workspaces";
import classNames from "../../../utils/classNames";
import BillingPage from "./Billing";
import Commissions from "./Commissions";
import DangerZonePage from "./DangerZone";
import GeneralPage from "./General";
import HostDashboardPage from "./HostDashboard";
import LabelsPage from "./Labels";
import MembersPage from "./Members";
import NotificationSubscriptionsPage from "./Notifications";

interface Props extends RouteComponentProps<{}> {}

const SettingsPage: React.FC<Props> = ({ match, location, history }) => {
  const { currentWorkspaceIsLocked } = useWorkspaces();
  const { isCurrentUserAdmin, membersLimitExceeded } = useMembers();

  const menu = useMemo(
    (): MenuItem[] => [
      {
        key: "workspace",
        title: "Workspace",
        tabs: compact([
          {
            key: "general",
            title: "Settings",
            Icon: CogIcon,
            hidden: currentWorkspaceIsLocked || membersLimitExceeded
          },
          {
            key: "billing",
            title: "Billing & Plans",
            Icon: CashIcon,
            hidden: false
          },
          {
            key: "members",
            title: "Members",
            Icon: UsersIcon,
            hidden: currentWorkspaceIsLocked
          },
          {
            key: "labels",
            title: "Labels",
            Icon: TagIcon,
            hidden: currentWorkspaceIsLocked || membersLimitExceeded
          },
          {
            key: "commissions",
            title: "Commissions",
            Icon: CurrencyDollarIcon,
            hidden: currentWorkspaceIsLocked || membersLimitExceeded
          },
          {
            key: "host-dashboard",
            title: "Host Dashboard",
            Icon: DesktopComputerIcon,
            hidden: currentWorkspaceIsLocked || membersLimitExceeded
          },
          {
            key: "notification-subscriptions",
            title: "Notifications",
            Icon: BellIcon,
            hidden: currentWorkspaceIsLocked || membersLimitExceeded
          },
          {
            key: "danger-zone",
            title: "Danger zone",
            Icon: ExclamationIcon,
            hidden: currentWorkspaceIsLocked || !isCurrentUserAdmin
          }
        ])
      }
    ],
    [currentWorkspaceIsLocked, isCurrentUserAdmin, membersLimitExceeded]
  );

  const pageMatch = matchPath<{
    section: string;
  }>(location.pathname, {
    path: "/workspace/settings/:section"
  });

  return (
    <div className="flex flex-col overflow-auto w-full max-w-7xl mx-auto px-4 sm:mt-6 sm:flex-row">
      <div className="sm:hidden block mb-4">
        <select
          className="w-full pl-3 py-2 sm:text-sm rounded-lg dark:bg-gray-800"
          onChange={(e) => {
            history.push(`${match.url}/${e.target.value}`);
          }}
          value={pageMatch?.params.section}
          data-testid="select-tab"
        >
          {menu.map(({ tabs }) =>
            tabs
              .filter((item) => !item.hidden)
              .map(({ title, key }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))
          )}
        </select>
      </div>

      <div className="hidden sm:flex flex-col sm:w-3/12">
        {menu.map(({ key, title, tabs }) => (
          <div
            className="flex flex-col mb-5 border border-gray-200 rounded-lg dark:border-gray-600 overflow-hidden"
            key={key}
          >
            {title && (
              <div className="px-5 py-2 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
                <span className="text-sm font-normal text-gray-600 dark:text-gray-300">
                  {title}
                </span>
              </div>
            )}
            {tabs
              .filter((item) => !item.hidden)
              .map(({ key, title, Icon }, index: number) => (
                <div
                  key={key}
                  className={classNames(
                    index !== tabs.length - 1 &&
                      "border-b border-gray-200 dark:border-gray-600",
                    "bg-white dark:bg-transparent dark:divide-gray-500"
                  )}
                >
                  <NavLink
                    to={`${match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-hotspotty-500 dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap flex py-3 px-4 border-l-2 border-transparent font-medium text-sm group dark:bg-gray-900"
                    data-testid="tab"
                  >
                    <Icon className="-ml-0.5 mr-2 h-5 w-5" />
                    <span>{title}</span>
                  </NavLink>
                </div>
              ))}
          </div>
        ))}
      </div>

      <div
        id="workspace-settings-container"
        className="flex sm:w-9/12 mb-4 sm:pl-6 sm:inline-flex overflow-y-auto overflow-x-hidden"
      >
        <Switch>
          <Redirect
            exact
            from="/workspace/settings"
            to={`${match.path}/general`}
          />

          <>
            <Route path={`${match.path}/general`} component={GeneralPage} />

            <Route path={`${match.path}/billing`} component={BillingPage} />

            {!currentWorkspaceIsLocked && (
              <Route path={`${match.path}/members`} component={MembersPage} />
            )}

            {isCurrentUserAdmin && (
              <Route
                path={`${match.path}/danger-zone`}
                component={DangerZonePage}
              />
            )}

            {!currentWorkspaceIsLocked && !membersLimitExceeded && (
              <>
                <Route path={`${match.path}/labels`} component={LabelsPage} />
                <Route
                  path={`${match.path}/commissions`}
                  component={Commissions}
                />
                <Route
                  path={`${match.path}/notification-subscriptions`}
                  component={NotificationSubscriptionsPage}
                />

                <Route
                  path={`${match.path}/host-dashboard`}
                  component={HostDashboardPage}
                />
              </>
            )}

            <Route
              exact
              path="/workspace/settings"
              render={() => <Redirect to={`${match.path}/general`} />}
            />
          </>
        </Switch>
      </div>
    </div>
  );
};

export default SettingsPage;

//
// Utils
//

interface Tab {
  key: string;
  title: string;
  Icon: React.ComponentType<any>;
  hidden: boolean | undefined;
}

interface MenuItem {
  key: string;
  title: string;
  tabs: Tab[];
}
